import type { CollabStatus } from "@docit/editor-core";
import { EditorV2, type EditorV2HostApi } from "@docit/editor-react";
import { createReact19WidgetRenderer } from "@docit/editor-react/render19";
import { CloudSlash, CloudWarning, Prohibit } from "@phosphor-icons/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useApi } from "~/api/provider";
import {
  useCollaborationToken,
  useCreateNudge,
  useEditorV2Lane,
  useScopedAiActions,
  useViewer,
} from "~/api/queries";
import type { Document } from "~/api/types";
import {
  collaborationTokenSource,
  collaborationUrl,
  isCrossOriginCollab,
} from "~/features/documents/collabConfig";
import { DocumentEditor } from "~/features/documents/editor/DocumentEditor";
import { useTaskizeTray } from "~/features/taskize/useTaskizeTray";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";

/**
 * The v2 lane's host (slate's re-expression of kms's `EditorV2Mount`).
 *
 * It mounts `@docit/editor-react` rather than rebuilding it: 3,880 lines of engine and skin
 * carry the provisional/committed visual language that IS the v2 product idea, and a second
 * implementation in Tailwind is how the two lanes drift. The host's job is the four things the
 * package deliberately does not do — auth, HTTP, toasts, and telling the truth about the
 * socket.
 *
 * ── Why the connection state is chrome and not a detail ──────────────────────────────────
 * A v2 document has NO REST save path: `documents.sculpt` refuses it with
 * `apply_lane_unsupported`, and `documents.info` serves the v1 text frozen at opt-in. The CRDT
 * room is the save. So if the socket never opens, every keystroke is discarded on close — and
 * a Hocuspocus provider retries a refused upgrade forever, with backoff, throwing nothing. An
 * editor that looked normal in that state would be a lie, which is why this renders the status
 * and, when the socket cannot be had, stops offering the surface at all and points back at the
 * lane that works.
 */

/** How long a socket may stay unconnected before we stop calling it "connecting". */
const CONNECT_GRACE_MS = 8000;

const TIME_ZONE =
  typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC" : "UTC";

export function EditorV2Host({
  document,
  canUpdate,
  onHostReady,
}: {
  document: Document;
  canUpdate: boolean;
  /**
   * W6.1 wire-up — the engine's host api (`getSelection` / `applyAtRange` / `documentEnd` /
   * `findText`), forwarded straight from `EditorV2`'s `onReady` so the document-AI ports can
   * write to the v2 lane. `EditorV2` already calls `onReady(null)` on unmount, so the scene's
   * state clears itself when the lane switches or the collaboration token disappears — and the
   * two early returns below never call it, which is exactly right: on those paths there IS no
   * editor, and the ports must say so rather than offer a write target that goes nowhere.
   */
  onHostReady?: (api: EditorV2HostApi | null) => void;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const { data: viewer } = useViewer();
  const lane = useEditorV2Lane(document.id);
  const tokenQuery = useCollaborationToken();
  // The merged, SCOPED catalogue — the unscoped list cannot see a document-scoped action.
  const actionsQuery = useScopedAiActions({
    documentId: document.id,
    ...(document.collectionId ? { projectId: undefined } : {}),
  });

  // W7 — the BubbleBar's ⏰ and its Taskize entry (§A3.2, §A2.1).
  const createNudge = useCreateNudge();
  const openTray = useTaskizeTray((state) => state.openTray);

  const [status, setStatus] = useState<CollabStatus | null>(null);
  const [graceElapsed, setGraceElapsed] = useState(false);

  // The renderer is per-host and stable: it keeps one React root per NodeView element, so
  // rebuilding it would orphan every mounted widget root.
  const widgetRenderer = useMemo(() => createReact19WidgetRenderer(), []);

  const collabUrl = useMemo(() => collaborationUrl(), []);
  const token = tokenQuery.data ?? null;
  // A host-supplied token SOURCE (the VS Code panel's short-lived, host-refreshed token) is passed
  // as a stable function, so a refresh reaches the socket's next (re)connect without remounting.
  const tokenSource = useMemo(() => collaborationTokenSource(), []);

  useEffect(() => {
    if (status === "connected") {
      return undefined;
    }
    setGraceElapsed(false);
    const timer = window.setTimeout(() => setGraceElapsed(true), CONNECT_GRACE_MS);
    return () => window.clearTimeout(timer);
  }, [status]);

  const onNotice = useCallback((level: "info" | "success" | "error", message: string) => {
    if (level === "error") {
      toast.error(message);
    } else if (level === "success") {
      toast.success(message);
    } else {
      toast.message(message);
    }
  }, []);

  /**
   * The dual-ACL gate. Returning `null` on failure is the contract, not laziness: `EditorV2`
   * retries with `requestedLane:"propose"` and surfaces exactly ONE notice if that is refused
   * too. Toasting here would produce two messages for one denial.
   */
  const authorizeAIAction = useCallback(
    async (request: {
      capability: string;
      from: number;
      to: number;
      requestedLane: "propose" | "act";
    }) => {
      try {
        const decision = await api.editorV2.aiAction(document.id, request);
        if (!decision) {
          return null;
        }
        return {
          allowed: decision.allowed,
          lane: decision.lane,
          participant: {
            kind: decision.participant.kind,
            id: decision.participant.id,
            displayName: decision.participant.displayName,
            ...(decision.participant.model ? { model: decision.participant.model } : {}),
          },
        };
      } catch {
        return null;
      }
    },
    [api, document.id],
  );

  /**
   * Runs the generation. Errors are RETHROWN with the server's own reason — swallowing a
   * refusal to `null` collapses a named ten-minute thinker timeout into "the AI did not return
   * a result", which is the single most misleading message this surface can show.
   *
   * A 202 means the resolved thinker is pinned to somebody's VS Code window: the work is
   * queued there, so this polls until the dispatch settles instead of failing.
   */
  const onAIAction = useCallback(
    async (request: { capability: string; instruction: string; selectedText: string }) => {
      const outcome = await api.selection.transform({
        documentId: document.id,
        ...(document.collectionId ? { collectionId: document.collectionId } : {}),
        documentTitle: document.title,
        instructions: request.instruction,
        selectedText: request.selectedText,
      });
      if (outcome.kind === "text") {
        return { text: outcome.text };
      }
      const text = await pollDispatch(api, outcome.dispatchId);
      return text === null ? null : { text };
    },
    [api, document.collectionId, document.id, document.title],
  );

  const deriver = useMemo(
    () => ({
      participant: {
        kind: "ai" as const,
        id: "thinker:deriver-default",
        displayName: t("Deriver"),
      },
      derive: async (span: { text: string; range: { from: number; to: number } }) => {
        const outcome = await api.editorV2.derive(document.id, {
          text: span.text,
          from: span.range.from,
          to: span.range.to,
        });
        return outcome.cards
          .filter((card) => card.statement.trim().length > 0)
          .map((card) => ({
            kind: card.kind === "decision" ? ("decision" as const) : ("truth" as const),
            statement: card.statement,
            source: span.range,
          }));
      },
    }),
    [api, document.id, t],
  );

  if (lane.isLoading || tokenQuery.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner label={t("Loading")} />
      </div>
    );
  }

  // No token means no socket, and no socket means no save path at all on this lane — so the
  // editor is not offered. Showing it would collect edits into a room nobody is listening to.
  if (!token || !viewer) {
    return (
      <UnavailablePanel
        title={t("Live collaboration is unavailable on this host")}
        detail={t(
          "The new editor saves through the collaboration service, and this deployment did not issue a collaboration token. Your document is unchanged — switch back to the old editor to keep working.",
        )}
        text={lane.data?.textV2 ?? document.text}
      />
    );
  }

  const refused = status === "unauthorized";
  const offline = status === "disconnected" || (status !== "connected" && graceElapsed);

  return (
    <div className="flex flex-col gap-3">
      <ConnectionBanner status={status} offline={offline} refused={refused} />

      {/* The engine and its skin. `styles.css` rides along with the package import. */}
      <EditorV2
        documentId={document.id}
        collab={{ url: collabUrl, token: tokenSource ?? token }}
        onCollabStatus={setStatus}
        {...(onHostReady ? { onReady: onHostReady } : {})}
        widgetRenderer={widgetRenderer}
        // Client-side lane preference only. The server 403s an `act` intent it will not grant
        // and the room independently forces the socket read-only, so this is never a bypass.
        lanePolicy={{ fallback: "act" }}
        currentUserId={`user:${viewer.id}`}
        currentUserName={viewer.name}
        {...(document.collectionId ? { scope: { collectionId: document.collectionId } } : {})}
        actions={(actionsQuery.data ?? [])
          .filter((action) => action.showInMenu)
          .map((action) => ({
            id: action.id,
            title: action.title,
            prompt: action.prompt,
            ...(action.icon ? { icon: action.icon } : {}),
            ...(action.shortcut ? { shortcut: action.shortcut } : {}),
          }))}
        placeholder={t("Start writing, or describe what this document should say.")}
        {...(lane.data?.stateV2 ? { seedStateBase64: lane.data.stateV2 } : {})}
        deriver={deriver}
        {...(canUpdate ? { authorizeAIAction, onAIAction } : {})}
        // W7 supplies the two props W6 was told it may leave undefined. The BubbleBar's ⏰ has
        // its own datetime popover, so it hands over a resolved ISO instant — always with a
        // zone, because the server parses `AssumeUniversal` and a naive string would be read
        // as UTC (§E2 R11). Email stays off: it is opt-in estate-wide.
        onCreateNudge={(dueIso, note) => {
          createNudge.mutate(
            {
              target: { targetType: "document", documentId: document.id },
              when: {
                whenUtc: new Date(dueIso).toISOString(),
                timezone: TIME_ZONE,
                ...(note.trim() ? { note: note.trim() } : {}),
              },
            },
            {
              onSuccess: () => toast.success(t("Nudge set")),
              onError: () => toast.error(t("Couldn't set the nudge")),
            },
          );
        }}
        // The tray is scene-owned and this host is inside the scene, so it can open directly.
        onTaskize={(selectedText) => openTray(document.id, selectedText)}
        onNotice={onNotice}
        // Slate has no analytics layer. Deliberately a no-op rather than a dropped prop, so
        // the metrics the engine already emits are visible the day one is added (§C6.4).
        onMetric={noopMetric}
      />
    </div>
  );
}

/** Placeholder analytics sink — see the note at the call site. */
function noopMetric(): void {
  /* no analytics layer in slate yet */
}

/**
 * Polls an IDE-pinned dispatch until it settles. Rethrows the window's own error rather than
 * resolving to nothing, for the same reason `onAIAction` rethrows.
 */
async function pollDispatch(
  api: ReturnType<typeof useApi>,
  dispatchId: string,
): Promise<string | null> {
  const deadline = Date.now() + 10 * 60_000;
  for (;;) {
    const dispatch = await api.selection.dispatchResult(dispatchId);
    if (api.selection.dispatchSettled(dispatch)) {
      if (dispatch.error) {
        throw new Error(dispatch.error);
      }
      return dispatch.transformedText;
    }
    if (Date.now() > deadline) {
      throw new Error("The editor window did not answer in time.");
    }
    await new Promise((resolve) => setTimeout(resolve, 1500));
  }
}

/**
 * The socket's state, in words. This is the only place a reader learns whether what they type
 * is being kept — so it says the state plainly and, when the endpoint is simply not routed on
 * this host, says that too rather than spinning forever.
 */
function ConnectionBanner({
  status,
  offline,
  refused,
}: {
  status: CollabStatus | null;
  offline: boolean;
  refused: boolean;
}) {
  const { t } = useTranslation();

  if (status === "connected") {
    return (
      <Text variant="small" className="text-text-tertiary">
        {t("Live — every change is saved as you type.")}
      </Text>
    );
  }

  if (refused) {
    return (
      <Notice
        tone="danger"
        icon={<Prohibit size={16} aria-hidden />}
        title={t("The collaboration service refused this session")}
        detail={t(
          "The collaboration token was rejected or has expired. Reload the page to get a fresh one; nothing you have typed since has been saved.",
        )}
      />
    );
  }

  if (offline) {
    return (
      <Notice
        tone="danger"
        icon={<CloudSlash size={16} aria-hidden />}
        title={t("Live collaboration unavailable on this host")}
        detail={
          isCrossOriginCollab()
            ? t(
                "The configured collaboration endpoint did not answer. Changes are not being saved — switch back to the old editor to keep working.",
              )
            : t(
                "This deployment does not route /collaboration yet, so the new editor cannot save. Changes are not being saved — switch back to the old editor to keep working.",
              )
        }
      />
    );
  }

  return (
    <Notice
      tone="muted"
      icon={<CloudWarning size={16} aria-hidden />}
      title={t("Connecting to live collaboration…")}
      detail={t("Changes are not saved until this connects.")}
    />
  );
}

function Notice({
  tone,
  icon,
  title,
  detail,
}: {
  tone: "danger" | "muted";
  icon: React.ReactNode;
  title: string;
  detail: string;
}) {
  return (
    <div
      role="status"
      className={
        tone === "danger"
          ? "flex items-start gap-2 rounded-md border border-danger/40 bg-danger/5 p-3"
          : "flex items-start gap-2 rounded-md border border-divider bg-bg-secondary p-3"
      }
    >
      <span className={tone === "danger" ? "mt-0.5 text-danger" : "mt-0.5 text-text-tertiary"}>
        {icon}
      </span>
      <div className="min-w-0">
        <Text variant="small" className="font-medium text-text">
          {title}
        </Text>
        <Text variant="small" className="text-text-tertiary">
          {detail}
        </Text>
      </div>
    </div>
  );
}

/**
 * What a reader sees when the v2 lane cannot be opened at all. It shows the document's last
 * server-projected v2 text READ-ONLY — honest about being a projection, never an editor that
 * silently discards work.
 */
function UnavailablePanel({
  title,
  detail,
  text,
}: {
  title: string;
  detail: string;
  text: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3">
      <Notice
        tone="danger"
        icon={<CloudSlash size={16} aria-hidden />}
        title={title}
        detail={detail}
      />
      {text.trim() ? (
        <DocumentEditor markdown={text} ariaLabel={t("Document content")} />
      ) : (
        <Text variant="secondary">{t("This document has no content yet.")}</Text>
      )}
    </div>
  );
}
